import React from "react";
import { Link } from "react-router-dom";
import { Eye, ShoppingCart } from "lucide-react";
import { useNotification } from "../Toast/Notification";

interface ProductCardProps {
  id: number;
  title: string;
  price: number;
  image: string;
  banner: string;
  isSelected: boolean;
  stock?: number;
  sku: string;
  isInCart: boolean;
  loading?: boolean;
  onAddToCart: (e: React.MouseEvent) => void;
  onViewCart: (e: React.MouseEvent) => void;
  onClick: (banner: string) => void;
}

const CylindersProduct: React.FC<ProductCardProps> = ({
  title,
  price,
  image,
  banner,
  isSelected,
  stock,
  isInCart,
  loading = false,
  onAddToCart,
  onViewCart,
  onClick,
}) => {
  const { notify } = useNotification();

  const handleButtonClick = (
    e: React.MouseEvent,
    callback: (e: React.MouseEvent) => void
  ) => {
    e.stopPropagation();
    
    if (!isInCart) {
      notify(`${title} successfully added to cart`, "success");
    }
    
    callback(e);
  };

  return (
    <div
      className={`rounded-lg w-full ${
        isSelected ? "border-2 border-[#75A7AD]" : "border border-gray-200"
      } bg-white shadow-md transition-all duration-300 hover:shadow-lg 
      cursor-pointer flex flex-col text-xs font-normal h-auto sm:h-[440px] overflow-hidden relative w-full sm:w-[300px]`}
      onClick={() => onClick(banner)}>
      <div className="p-4 pb-0">
        <h3 className="text-[#002238] text-[16px] font-semibold mb-1 ">
          {title}
        </h3>
        <p className="text-[#212529] text-lg font-bold mb-3">
          R {price.toFixed(2)}
        </p>
      </div>

      <div className="flex justify-center items-center pt-4 px-4 h-[180px] sm:h-[230px]">
        <img src={image} alt={title} className="h-full object-contain" />
      </div>

      <div className="flex justify-center">
        {loading ? (
          <div className="inline-flex mt-[8%] items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#31526B] text-white">
            <div className="w-2 h-2 rounded-full bg-white mr-1.5 animate-pulse"></div>
            Checking stock...
          </div>
        ) : (
          <div
            className={`inline-flex mt-[8%] items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              (stock || 0) > 0
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}>
            {(stock || 0) > 0 ? (
              <>
                <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>{" "}
                In Stock
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></span>{" "}
                Out of Stock
              </>
            )}
          </div>
        )}
      </div>

      <div className="mt-auto p-4 bg-white">
        {loading ? (
          <button
            disabled
            className="w-full bg-gray-300 text-gray-500 py-2.5 px-4 rounded-md font-medium flex items-center justify-center cursor-not-allowed">
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-t-2 border-gray-500 rounded-full animate-spin mr-2"></div>
              Loading...
            </div>
          </button>
        ) : (stock || 0) <= 0 ? (
          <Link
            to="/store-locator"
            onClick={(e) => e.stopPropagation()}
            className="w-full">
            <button className="w-full bg-[#31526B] hover:bg-[#264053] text-white py-2.5 px-4 rounded-md font-medium flex items-center justify-center transition-colors">
              Store Locator
            </button>
          </Link>
        ) : (
          <button
            className="w-full bg-[#31526B] hover:bg-[#264053] text-white py-2.5 px-4 rounded-md font-medium flex items-center justify-center transition-colors"
            onClick={(e) =>
              handleButtonClick(e, isInCart ? onViewCart : onAddToCart)
            }>
            {isInCart ? (
              <>
                <Eye size={18} className="mr-2" />
                View Cart
              </>
            ) : (
              <>
                <ShoppingCart size={18} className="mr-2" />
                Add to Cart
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CylindersProduct;